import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Paper, 
  Grid, 
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Chip
} from '@mui/material';
import { Add as AddIcon, AttachFile as AttachFileIcon } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { useTranslation } from '../../commons/useTranslasion';
import { useSolicitacaoActions } from '../../states/solicitacoes/solicitacao.actions';
import { ISolicitacao } from '../../commons/types/solicitacoes';
import { useDateFormatters } from '../../utils/useDateFormatters';

const ListaSolicitacoes: React.FC = () => {
  const { translate } = useTranslation();
  const { formatarDataHora } = useDateFormatters();
  const history = useHistory();
  const solicitacaoActions = useSolicitacaoActions();
  const [solicitacoes, setSolicitacoes] = useState<ISolicitacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    carregarSolicitacoes();
  }, []);

  const carregarSolicitacoes = async () => {
    try {
      setLoading(true);
      const response = await solicitacaoActions.listarSolicitacoes();
      setSolicitacoes(response || []);
      setError(null);
    } catch (err) {
      setError(typeof err === 'string' ? err : translate('erroAoBuscarSolicitacoes'));
    } finally {
      setLoading(false);
    }
  };

  const handleNovaSolicitacao = () => {
    history.push('/solicitacoes/nova');
  };

  const handleVerDetalhes = (id: number) => {
    history.push(`/solicitacoes/${id}`);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'backlog':
        return '#9e9e9e';
      case 'em andamento':
        return '#2196f3';
      case 'concluído':
        return '#4caf50';
      case 'cancelado':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  const formatarData = (dataString: string) => {
    try {
      return formatarDataHora(dataString);
    } catch (e) {
      return dataString;
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h4" component="h1" gutterBottom>
              {translate('minhasSolicitacoes')}
            </Typography>
          </Grid>
          <Grid item>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={handleNovaSolicitacao}
            >
              {translate('novaSolicitacao')}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>{translate('carregandoSolicitacoes')}</Typography>
          </Box>
        ) : error ? (
          <Paper sx={{ p: 4, bgcolor: '#fff3f3', color: '#d32f2f', mt: 2 }}>
            <Typography>{error}</Typography>
          </Paper>
        ) : solicitacoes.length === 0 ? (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography variant="h6" gutterBottom>
              {translate('semSolicitacoes')}
            </Typography>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleNovaSolicitacao}
              sx={{ mt: 2 }}
            >
              {translate('criarPrimeiraSolicitacao')}
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {solicitacoes.map((solicitacao) => (
              <Grid item xs={12} key={solicitacao.id}>
                <Card 
                  sx={{ 
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)', 
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                    },
                    overflow: 'visible',
                    position: 'relative'
                  }}
                >
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: '-12px', 
                      left: '16px', 
                      bgcolor: 'primary.main', 
                      color: 'white',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    {solicitacao.identificador}
                  </Box>
                  
                  <CardHeader
                    title={
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 1 }}>
                        {solicitacao.titulo}
                      </Typography>
                    }
                    action={
                      <Chip 
                        label={solicitacao.status} 
                        sx={{ 
                          bgcolor: solicitacao.statusCor + '20' || getStatusColor(solicitacao.status) + '20',
                          color: getStatusColor(solicitacao.status),
                          fontWeight: 'bold',
                          mt: 1
                        }} 
                      />
                    }
                  />
                  
                  <CardContent sx={{ pt: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 2
                    }}>
                      {solicitacao.descricao}
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mt: 2,
                      pt: 2,
                      borderTop: '1px solid #f0f0f0'
                    }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium', display: 'block' }}>
                          {translate('dataRegistro')}:
                        </Typography>
                        <Typography variant="body2">
                          {formatarData(solicitacao.dataCriacao)}
                        </Typography>
                      </Box>
                      
                      {solicitacao.anexos && solicitacao.anexos.length > 0 && (
                        <Chip 
                          size="small" 
                          icon={<AttachFileIcon fontSize="small" />}
                          label={`${solicitacao.anexos.length} ${solicitacao.anexos.length === 1 ? translate('anexo') : translate('anexos')}`} 
                          sx={{ fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  </CardContent>
                  
                  <CardActions sx={{ borderTop: '1px solid #f0f0f0', justifyContent: 'flex-end' }}>
                    <Button 
                      variant="contained"
                      color="primary"
                      size="small" 
                      onClick={() => handleVerDetalhes(solicitacao.id)}
                    >
                      {translate('detalhesSolicitacao')}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default ListaSolicitacoes;
